import { useTranslations } from '@aikoa/i18n/react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'expo-router'
import { useState } from 'react'
import { Pressable, View } from 'react-native'
import ContentBox from '~/components/layout/ContentBox'
import ScreenView from '~/components/layout/ScreenView'
import { Body, Label } from '~/components/typography/Content'
import { Heading2 } from '~/components/typography/Titles'
import { Button, ButtonLabel, LinkButton } from '~/components/ui/button'
import { Text } from '~/components/ui/text'
import { trpc } from '~/lib/trpc'

type Promotion = {
  id: string
  promotion_code: string
  title_key: string
  promotion_amount: number
  promotion_type: 'free_months' | 'percentage' | 'amount'
  available_from: string
  available_to: string | null
  valid_duration: string | null
  deactivated_at: string | null
  created_at: string
}

function PromotionRow({ promotion }: { promotion: Promotion }) {
  const { t } = useTranslations('admin')

  const now = new Date()
  const availableFrom = new Date(promotion.available_from)
  const availableTo = promotion.available_to ? new Date(promotion.available_to) : null

  const isDeactivated = promotion.deactivated_at !== null
  const isExpired = !isDeactivated && availableTo && availableTo < now
  const isNotYetActive = !isDeactivated && availableFrom > now

  const formatAmount = () => {
    switch (promotion.promotion_type) {
      case 'free_months':
        return `${promotion.promotion_amount} ${t(($) => $.promotions.types.freeMonthsUnit)}`
      case 'percentage':
        return `${promotion.promotion_amount}%`
      case 'amount':
        return `${promotion.promotion_amount} €`
      default:
        return String(promotion.promotion_amount)
    }
  }

  return (
    <Link href={`/(authenticated)/admin/promotions/${promotion.id}`} asChild>
      <Pressable className="flex-row items-center justify-between gap-4 rounded-lg border border-neutral-200 bg-white p-4 active:bg-neutral-50">
        <View className="flex-1 gap-1">
          <View className="flex-row items-center gap-2">
            <Text className="font-medium text-base">{promotion.promotion_code}</Text>
            {isDeactivated && (
              <View className="rounded bg-neutral-200 px-2 py-0.5">
                <Text className="text-xs text-neutral-600">
                  {t(($) => $.promotions.status.deactivated)}
                </Text>
              </View>
            )}
            {isExpired && (
              <View className="rounded bg-red-100 px-2 py-0.5">
                <Text className="text-xs text-red-700">
                  {t(($) => $.promotions.status.expired)}
                </Text>
              </View>
            )}
            {isNotYetActive && (
              <View className="rounded bg-yellow-100 px-2 py-0.5">
                <Text className="text-xs text-yellow-700">
                  {t(($) => $.promotions.status.scheduled)}
                </Text>
              </View>
            )}
          </View>
          <Label className="text-sm text-secondary">
            {t(($) => $.promotions.types[promotion.promotion_type])}: {formatAmount()}
          </Label>
        </View>
      </Pressable>
    </Link>
  )
}

const PAGE_SIZE = 20

export default function PromotionsIndex() {
  const { t } = useTranslations('admin')
  const [page, setPage] = useState(1)

  const queryInput = { page, limit: PAGE_SIZE }

  const { data, isLoading, error, isFetching } = useQuery(
    trpc.admin.listPromotions.queryOptions(queryInput)
  )

  const promotions = data?.promotions ?? []
  const totalPages = data?.totalPages ?? 1
  const totalCount = data?.totalCount ?? 0
  const currentPage = data?.page ?? page

  const canGoBack = currentPage > 1
  const canGoNext = currentPage < totalPages

  return (
    <ScreenView>
      <ContentBox className="flex-1" contentContainerClassName="gap-6" variant="transparent">
        <View className="gap-4">
          <Heading2 className="text-center">{t(($) => $.promotions.title)}</Heading2>
          <Body className="text-center text-neutral-600">{t(($) => $.promotions.description)}</Body>
        </View>

        <LinkButton href="/(authenticated)/admin/promotions/add" variant="primary" size="sm">
          <ButtonLabel>{t(($) => $.promotions.createButton)}</ButtonLabel>
        </LinkButton>

        {isLoading && (
          <Body className="text-center text-neutral-500">{t(($) => $.promotions.loading)}</Body>
        )}

        {!!error && (
          <Body className="text-center text-red-600">{t(($) => $.promotions.loadError)}</Body>
        )}

        {!isLoading && !error && promotions.length === 0 && (
          <Body className="text-center text-neutral-500">{t(($) => $.promotions.none)}</Body>
        )}

        {promotions.length > 0 && (
          <>
            <View className="gap-3">
              {promotions.map((promotion) => (
                <PromotionRow key={promotion.id} promotion={promotion as Promotion} />
              ))}
            </View>

            <View className="flex-row items-center justify-between gap-4 pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!canGoBack || isFetching}
                onPress={() => setPage(currentPage - 1)}>
                <ButtonLabel>{t(($) => $.promotions.previous)}</ButtonLabel>
              </Button>

              <Text className="text-sm text-neutral-600">
                {t(($) => $.promotions.page, {
                  current: currentPage,
                  total: totalPages,
                  count: totalCount,
                })}
              </Text>

              <Button
                variant="outline"
                size="sm"
                disabled={!canGoNext || isFetching}
                onPress={() => setPage(currentPage + 1)}>
                <ButtonLabel>{t(($) => $.promotions.next)}</ButtonLabel>
              </Button>
            </View>
          </>
        )}
      </ContentBox>
    </ScreenView>
  )
}
